"use client";

import React, { useEffect, useRef } from "react";
import { ClientSideNewProductCard } from "@/components/NewProductCard";
import { ProductSummary } from "@/common/productSummary/types";
import Image from "next/image";
import { LoadingSpinner } from "@/components/icons";
import { useGuidedPathQuiz } from "@/i18n/hooks/useGuidedPathQuiz";
import { useGuidedPathResultsMessages } from "@/i18n/hooks/useGuidedPathResultsMessages";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { ElementViewed } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementViewed";
import { useInView } from "react-intersection-observer";
import { ElementType } from "@/constants/tagular/main";
import { getLevel } from "@/components/Search/utils/getLevel";

interface FindYourPathResultsProps {
  searchResults?: ProductSummary[];
  quizAnswers?: Record<string, string | string[]>;
  onEditGoal?: () => void;
  isLoading?: boolean;
  shouldFocusHeading?: boolean;
  onHeadingFocused?: () => void;
}

const FindYourPathResults = ({
  searchResults = [],
  quizAnswers = {},
  onEditGoal,
  isLoading = false,
  shouldFocusHeading = false,
  onHeadingFocused,
}: FindYourPathResultsProps) => {
  const quizConfig = useGuidedPathQuiz();
  const resultsMessages = useGuidedPathResultsMessages();
  const headingRef = useRef<HTMLHeadingElement>(null);

  // No results button element tracking
  const noResultsButtonWebElement: ElementViewed["webElement"] = {
    elementType: ElementType.Button,
    text: resultsMessages.startOver,
    name: "no-results-found",
  };

  const {
    elementViewed: noResultsButtonElementViewed,
    elementClicked: noResultsButtonElementClicked,
  } = useElementEvents({ webElement: noResultsButtonWebElement });

  const { ref: noResultsButtonRef, inView: noResultsButtonInView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  // Edit goal button element tracking
  const editGoalButtonWebElement: ElementViewed["webElement"] = {
    elementType: ElementType.Button,
    text: resultsMessages.editGoal,
    name: "edit-goal",
  };

  const {
    elementViewed: editGoalButtonElementViewed,
    elementClicked: editGoalButtonElementClicked,
  } = useElementEvents({ webElement: editGoalButtonWebElement });

  const { ref: editGoalButtonRef, inView: editGoalButtonInView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  useEffect(() => {
    if (noResultsButtonInView) {
      noResultsButtonElementViewed();

      // Track no results event in New Relic
      if (typeof window !== "undefined" && window.newrelic) {
        window.newrelic.addPageAction("FindYourPathResults.noResultsShown", {
          quizAnswers: JSON.stringify(quizAnswers),
          timestamp: new Date().toISOString(),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noResultsButtonInView, noResultsButtonElementViewed]);

  useEffect(() => {
    if (editGoalButtonInView) {
      editGoalButtonElementViewed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editGoalButtonInView, editGoalButtonElementViewed]);

  useEffect(() => {
    if (!shouldFocusHeading || isLoading) {
      return;
    }

    const headingElement = headingRef?.current;
    if (headingElement) {
      headingElement.focus();
      onHeadingFocused?.();
    }
  }, [headingRef, isLoading, onHeadingFocused, shouldFocusHeading]);

  const handleNoResultsButtonClick = () => {
    noResultsButtonElementClicked();
    onEditGoal?.();
  };

  const handleEditGoalButtonClick = () => {
    editGoalButtonElementClicked();
    onEditGoal?.();
  };

  // Helper function to get label from value
  const getLabel = (key: string, value: string): string => {
    if (!quizConfig) return value;

    const section = quizConfig.sections.find(
      (s) => (s.searchFilterKey || s.key) === key,
    );
    if (!section?.options) return value;

    const option = section.options.find(
      (opt) =>
        opt.value === value ||
        opt.searchFilterValue === value ||
        opt.label === value,
    );
    return option?.label || value;
  };

  // Helper function to get message from value (falls back to lowercase label)
  const getMessage = (key: string, value: string): string => {
    if (!quizConfig) return value.toLowerCase();

    const section = quizConfig.sections.find(
      (s) => (s.searchFilterKey || s.key) === key,
    );
    if (!section?.options) return value.toLowerCase();

    const option = section.options.find(
      (opt) =>
        opt.value === value ||
        opt.searchFilterValue === value ||
        opt.label === value,
    );
    return (
      option?.message || option?.label?.toLowerCase() || value.toLowerCase()
    );
  };

  // Generate personalized message based on quiz answers
  const generatePersonalizedMessage = () => {
    // Get career stage with article
    const careerStageValue = quizAnswers["career-stage"];
    const careerStageLabel = careerStageValue
      ? getLabel("career-stage", careerStageValue as string)
      : "";
    const careerStageArticle =
      careerStageValue === "associate"
        ? resultsMessages.careerStageArticleAn
        : resultsMessages.careerStageArticleA;
    const careerStageText = careerStageLabel
      ? `${careerStageArticle} ${careerStageLabel}`
      : "";

    // Get field of study (attributes)
    const attributesValue = quizAnswers["attributes"];
    const attributesText = Array.isArray(attributesValue)
      ? attributesValue[0] || ""
      : attributesValue || "";

    // Get skills - format multiple values with commas and "and"
    const skillsValue = quizAnswers["skills"];
    let skillsText = "";
    if (Array.isArray(skillsValue) && skillsValue.length > 0) {
      if (skillsValue.length === 1) {
        skillsText = skillsValue[0];
      } else if (skillsValue.length === 2) {
        skillsText = `${skillsValue[0]} and ${skillsValue[1]}`;
      } else {
        const lastSkill = skillsValue[skillsValue.length - 1];
        const otherSkills = skillsValue.slice(0, -1).join(", ");
        skillsText = `${otherSkills}, and ${lastSkill}`;
      }
    } else if (typeof skillsValue === "string") {
      skillsText = skillsValue;
    }

    // Get goal message
    const goalValue = quizAnswers["goal"];
    const goalMessage = goalValue
      ? getMessage("goal", goalValue as string)
      : resultsMessages.defaultGoalMessage;

    // Build the personalized message
    const description =
      careerStageText && attributesText && skillsText
        ? resultsMessages.personalizedDescription(
            careerStageText,
            skillsText,
            goalMessage,
          )
        : resultsMessages.fallbackDescription(goalMessage);

    return {
      title: resultsMessages.recommendedPathsTitle(goalMessage),
      description,
    };
  };

  // Show loading state while searching
  if (isLoading) {
    return (
      <div className="font-sans flex flex-col items-center justify-center min-h-[400px] p-4 md:p-8 bg-putty-300 rounded-xl">
        <div className="relative mb-6 animate-spin">
          <LoadingSpinner />
        </div>
        <span className="text-base text-gray-900 mx-auto">
          {resultsMessages.personalizing}
        </span>
      </div>
    );
  }

  const personalizedMessage = generatePersonalizedMessage();

  // Show "no results" message if search completed with 0 results
  if (!isLoading && searchResults.length === 0) {
    return (
      <div className="font-sans flex flex-col items-center justify-center min-h-[400px] p-4 md:p-8 bg-putty-300 rounded-xl">
        <h3
          className="text-xl font-bold text-gray-900 mb-4"
          ref={headingRef}
          tabIndex={-1}
        >
          {resultsMessages.noResultsTitle}
        </h3>
        <p className="text-gray-600 mb-6">
          {resultsMessages.noResultsDescription}
        </p>
        {onEditGoal && (
          <button
            ref={noResultsButtonRef}
            onClick={handleNoResultsButtonClick}
            className="bg-brand-500 text-white rounded-full py-3 px-6 hover:bg-brand-600"
          >
            {resultsMessages.startOver}
          </button>
        )}
      </div>
    );
  }

  // Show search results
  return (
    <div className="font-sans flex flex-col gap-4 max-w-7xl mx-auto bg-putty-300 rounded-xl p-6 md:p-8">
      <div className="flex flex-col text-start gap-4">
        <div className="flex flex-col md:flex-row md:justify-between gap-4 md:gap-0">
          <div className="flex items-center gap-2">
            <div className="hidden md:block">
              <Image
                src="/icons/ai-icon.png"
                alt={resultsMessages.aiIconAlt}
                width={36}
                height={36}
                className="m-0"
              />
            </div>
            <h3
              className="text-xl font-bold text-gray-900 m-0"
              ref={headingRef}
              tabIndex={-1}
            >
              {personalizedMessage.title}
            </h3>
          </div>
          <div className="flex items-center">
            <button
              ref={editGoalButtonRef}
              onClick={handleEditGoalButtonClick}
              className="not-prose bg-white text-gray-900 rounded-full transition-all  py-2 px-5 hover:bg-gray-50"
            >
              <div className="font-bold text-base flex items-center gap-2 justify-center text-center">
                {resultsMessages.editGoal}
              </div>
            </button>
          </div>
        </div>
        <p className="m-0">{personalizedMessage.description}</p>
        <span>{resultsMessages.exploreOptions}</span>
      </div>

      {/* Search Results using NewProductCard */}
      <div className="not-prose grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {searchResults.slice(0, 4).map((result, index) => (
          <ClientSideNewProductCard
            key={result.productUuid || index}
            name={result.productName}
            href={result.productSlug ? `/${result.productSlug}` : ""}
            imageUrl={result.productImageUrl}
            type={result.productType}
            partnerFullName={
              Array.isArray(result.partnerName)
                ? result.partnerName[0]
                : result.partnerName
            }
            subjects={result.attributes || undefined}
            level={getLevel(result.level ?? undefined)}
            location="guided-path-results"
            index={index}
            uuid={result.productUuid}
            partnerLogoUrl={result.partnerLogoUrl}
            weeksToComplete={result.weeksToComplete || undefined}
            productCardType={{
              singleCourseTags: [{ tags: "Level" }, { tags: "CompletionTime" }],
              programTags: [
                { tags: "Level" },
                { tags: "CompletionTime" },
                { tags: "CourseCount" },
              ],
              execEdTags: [{ tags: "Level" }, { tags: "CompletionTime" }],
              degreeTags: [{ tags: "Level" }, { tags: "CompletionTime" }],
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default FindYourPathResults;
